// needs to support both client and server because of event grid (client) and event slider (server)
import styles from "./EventCard.module.scss";
import { format, formatISO, isBefore, parse } from "date-fns";
import { customSanitize } from "../../utils/customSanitize";
import { optimizeURL } from "../../utils/url";
import { ContentTag } from "../ContentTag/ContentTag";
import { Modul_Termin_Grid } from "@aleksundshantu/gp-types/acf-module";
import { Button } from "../Button/Button";
import { GetLabel } from "@/getData/fetchData.server";
import { getDateFnsLocale } from "@/utils/getDateFnsLocale";
import CardLinkOverlay from "@/components/CardLinkOverlay/CardLinkOverlay.tsx";

interface EventCardProps {
  headlineTag: Modul_Termin_Grid["events_grid"]["headline_html_tag"];
  element: EventCardType;
  show_border_radius: boolean;
  show_box_shadow: boolean;
  showBox: boolean;
  moduleType: "events-slider" | "events-grid" | "";
  skeleton?: boolean;
  showExcerpt?: boolean;
  datePosition?: "top-right" | "left-of-time";
  getLabel: GetLabel;
  locale: string;
}

export type EventCardType = {
  id: number;
  date: string;
  start_time?: string;
  end_time?: string;
  title?: string;
  excerpt?: string;
  link: string;
};

const truncateExcerpt = (excerpt: string, maxLength: number) => {
  if (!excerpt) return "";

  // If the excerpt is shorter than the max length, return it as is
  if (excerpt.length <= maxLength) return excerpt;

  // Find the last space within the max length
  const truncated = excerpt.slice(0, maxLength);
  const lastSpaceIndex = truncated.lastIndexOf(" ");

  // If there's no space within the max length, return truncated string with ellipsis
  if (lastSpaceIndex === -1) return truncated + "...";

  // Otherwise, return truncated string up to the last space with ellipsis
  return truncated.slice(0, lastSpaceIndex) + "...";
};

// Function to convert 12-hour format to 24-hour format
const convert12To24Hour = (time: string | undefined) => {
  if (!time) return undefined;

  // Parse the time assuming it's in 'h:mm a' (12-hour) format (e.g., 2:30 PM)
  const parsedTime = parse(time, "h:mm a", new Date());

  // Format it to 24-hour format (e.g., 14:30)
  return format(parsedTime, "HH:mm");
};

export const EventCard = ({
  headlineTag,
  element,
  show_border_radius,
  show_box_shadow,
  showBox,
  moduleType,
  skeleton = false,
  showExcerpt = true,
  datePosition = "top-right",
  getLabel,
  locale,
}: EventCardProps) => {
  const link = element.link;

  const eventDateObj = parse(element.date, "dd.MM.yyyy", new Date());
  const today = new Date();
  const isOldEvent = isBefore(eventDateObj, today);

  const readMoreLabel = getLabel("read_more_label");
  const startTimeLabel = getLabel("start_time");
  const endTimeLabel = getLabel("end_time");

  const start_time = element.start_time
    ? convert12To24Hour(element.start_time)
    : undefined;
  const end_time = element.end_time
    ? convert12To24Hour(element.end_time)
    : undefined;

  const excerpt = element.excerpt
    ? truncateExcerpt(element.excerpt || "", 256)
    : undefined;

  // format date to ISO 8601 format
  const eventDate = formatISO(eventDateObj);

  const skeletonElementClass = skeleton ? "skeleton-element" : "";

  const EventDate = () => (
    <time
      dateTime={eventDate}
      className={`${styles["embla-slide-date"]} date ${datePosition === "left-of-time" ? "pr-1" : ""}`}
    >
      <span className={`${skeletonElementClass}`}>
        {format(eventDateObj, "dd. MMMM yyyy", {
          locale: getDateFnsLocale(locale),
        })}
      </span>
    </time>
  );

  const formatTime = () => (
    <div>
      {datePosition === "left-of-time" && <EventDate />}
      <section className={`${styles["embla-slide-time"]} date`}>
        <time dateTime={start_time} aria-label={startTimeLabel}>
          {start_time}
        </time>

        {end_time && (
          <p className="date">
            –{" "}
            <time dateTime={end_time} aria-label={endTimeLabel}>
              {end_time}
            </time>
          </p>
        )}

        {locale === "de" && <p className="date">Uhr</p>}
      </section>
    </div>
  );

  return (
    <div
      data-module-type={`${moduleType}-${isOldEvent ? "old" : "new"}`}
      className={`${styles["embla-slide"]} embla-slide ${showBox ? "is-box" : ""}
    ${show_border_radius ? "has-border-radius" : ""} ${show_box_shadow ? "has-box-shadow" : ""} ${moduleType ? "pt-0" : ""}
    `}
    >
      <CardLinkOverlay title={element.title} link={link} getLabel={getLabel} />
      <div className={`${styles["embla-slide-inner"]}`}>
        <article className={styles["embla-slide-inner-content"]}>
          {datePosition === "top-right" && <EventDate />}

          {start_time ? formatTime() : null}

          {element.title && (
            <span
              role="heading"
              aria-level={headlineTag === "h3" ? 4 : 3}
              className={`${styles["embla-slide-title"]} title has-text-left ${skeletonElementClass}`}
              dangerouslySetInnerHTML={{
                __html: customSanitize(element.title),
              }}
            />
          )}

          {excerpt && showExcerpt && (
            <ContentTag
              content={excerpt}
              extraClass={`${skeletonElementClass}`}
            />
          )}
        </article>

        <Button
          type="secondary"
          href={optimizeURL(link) || link}
          className={`${skeletonElementClass} ${!showExcerpt ? "mt-0" : ""} card-inner-link-button`}
          target="_self"
          aria-label={`${readMoreLabel}: ${element.title}`}
          disablePrefetch // Disable prefetching for grid links https://trello.com/c/g5k075FS
        >
          {readMoreLabel}
        </Button>
      </div>
    </div>
  );
};
